import {
  CookieKey,
  GAS_ACCOUNT_API_BASE_URL,
  V2_GAS_ACCOUNT_API_BASE_URL,
} from "@/configs/common"
import { handleUnauthorizedResponse } from "./unauthorized-response-handler"
import axios, {
  type AxiosPromise,
  type Axios,
  type AxiosError,
  type AxiosResponse,
  type AxiosRequestConfig,
} from "axios"
import { cookieStorage } from "wagmi"
import { jwtDecode } from "jwt-decode"

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded: { exp: number } = jwtDecode(token)
    return decoded.exp < Math.floor(Date.now() / 1000)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return true
  }
}

export interface MyAxiosInstance extends Axios {
  (config: AxiosRequestConfig): AxiosPromise
  (url: string, config?: AxiosRequestConfig): AxiosPromise
}

function createAuthorizedGasAccountRequest(baseURL: string): MyAxiosInstance {
  const instance: MyAxiosInstance = axios.create({
    baseURL,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Credentials": "true",
      "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH",
    },
  })

  instance.interceptors.request.use(
    (config) => {
      const token = cookieStorage.getItem(CookieKey.AuthToken)

      if (!token) {
        throw new Error("No valid token available")
      }

      if (isTokenExpired(token)) {
        cookieStorage.removeItem(CookieKey.AuthToken)
        throw new Error("Token has expired")
      }

      const newConfig = { ...config }
      newConfig.headers.Authorization = `Bearer ${token}`
      return newConfig
    },
    async function (error) {
      return await Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response.data
    },
    (error: AxiosError) => {
      handleUnauthorizedResponse(error)
      throw error.response?.data
    }
  )

  return instance
}

const authorizedRequestGasAccount = createAuthorizedGasAccountRequest(
  GAS_ACCOUNT_API_BASE_URL
)

export const authorizedRequestGasAccountV2 = createAuthorizedGasAccountRequest(
  V2_GAS_ACCOUNT_API_BASE_URL
)

export default authorizedRequestGasAccount
